import fillOut_img from '../../../../assets/fillOutQuestionnaire.svg';
import waiting_empty_img from '../../../../assets/waiting_empty.svg';
import { Text } from '@mantine/core';
import { DateTime } from 'luxon';
import {
  Bdm1Outcome,
  CohortType,
  RetrievalMode,
  StudyGroup,
} from '@prisma/client';
import {
  PART2_RANDOMIZATION_DAY_OFFSET,
  SAMPLE_LOCATION_NAME,
  bdmDayStarted,
} from '@web/common';
import type { Bdm2OutcomeResDto, Bdm2OutcomeVariant } from '@web/common';
import { StepKey } from './stepTypes';
import type { Bdm2Outcome, StepDataType } from './stepTypes';

// Maps each study group to its translation-key segment (Steps.2.<key>.*).
// `satisfies Record<StudyGroup, ...>` makes this exhaustive: adding a study
// group forces a matching translation key here instead of silently defaulting.
const STUDY_GROUP_STEP_KEY = {
  [StudyGroup.GROUP_1]: 'group1',
  [StudyGroup.GROUP_2]: 'group2',
} as const satisfies Record<StudyGroup, string>;

// Maps each BDM1 outcome to its translation-key segment (Steps.bdm1.<key>.*).
const BDM1_OUTCOME_STEP_KEY = {
  [Bdm1Outcome.VOUCHER]: 'voucher',
  [Bdm1Outcome.SAMPLE_LOCAL]: 'sample_local',
  [Bdm1Outcome.SAMPLE_MAIL]: 'sample_mail',
} as const satisfies Record<Bdm1Outcome, string>;

// Maps each backend BDM2 variant to its translation-key segment (Steps.bdm2.<key>.*).
// The two namings differ on purpose: the backend names the fulfilment (pickup/mail),
// the approved copy keys were written as local_/mail_ and voucher.
const BDM2_OUTCOME_STEP_KEY = {
  pickupNoCash: 'local_no_cash',
  pickupCash: 'local_cash',
  pickupFullProduct: 'local_full_product',
  mailNoCash: 'mail_no_cash',
  mailCash: 'mail_cash',
  mailFullProduct: 'mail_full_product',
  noProduct: 'voucher',
} as const satisfies Record<Bdm2OutcomeVariant, NonNullable<Bdm2Outcome>>;

// Shared between the allSteps gates below and WaitingStateStore.currentStepKey
// so the step array and the active-step selection cannot drift apart.
export const isBdm1StepVisible = (
  studyGroup: StudyGroup | null,
  bdm1SurveyFilledOut: boolean,
  bdm1Outcome: Bdm1Outcome | null,
): boolean =>
  studyGroup === StudyGroup.GROUP_1 &&
  (bdm1SurveyFilledOut || bdm1Outcome !== null);

export const isBdm2StepVisible = (
  studyGroup: StudyGroup | null,
  bdm2SurveyDue: boolean,
  bdm2SurveyFilledOut: boolean,
  bdm2Outcome: Bdm2OutcomeResDto | null,
): boolean =>
  studyGroup === StudyGroup.GROUP_1 &&
  (bdm2SurveyDue || bdm2SurveyFilledOut || bdm2Outcome !== null);

// step-3 is only shown when all tasks of the user are done. It may never be shown
// before the cohorts are assigned — then step-4 wins and step-3 is skipped.
export const isStep3Visible = (
  studyGroup: StudyGroup | null,
  cohortType: CohortType | null,
  hasOpenGroup1Survey: boolean,
  bdmStartDate: Date | null,
  bdm2RetrievalSettled: boolean,
): boolean => {
  if (studyGroup === null || cohortType !== CohortType.NONE) {
    return false;
  }
  // we can only show step 3 once the randomization offset is hit
  if (!bdmDayStarted(bdmStartDate, PART2_RANDOMIZATION_DAY_OFFSET)) {
    return false;
  }
  // true for group 2 since they don't have BDM tasks; for group 1 the open tasks
  // (survey, retrieval) need to be settled first
  return (
    studyGroup === StudyGroup.GROUP_2 ||
    (!hasOpenGroup1Survey && bdm2RetrievalSettled)
  );
};

const descriptionLong = (parts: string[]) => (
  <Text size="16px" lh="24px" maw="650px" style={{ whiteSpace: 'pre-line' }}>
    {parts.join('\n\n')}
  </Text>
);

type StepsDataOptions = {
  dateTime: DateTime;
  cohortType: CohortType;
  // "Anfang/Mitte/Ende <Monat> <Jahr>" of the group assignment; null when the cohort (and
  // with it the onboarding end date) is not known yet — the sentence then drops the date
  groupAssignmentDate: string | null;
  studyGroup: StudyGroup | null;
  bdm1SurveyFilledOut: boolean;
  bdm1Outcome: Bdm1Outcome | null;
  bdm1SampleDeadline: DateTime | null;
  retrievalMode: RetrievalMode | null;
  bdm2SurveyDue: boolean;
  bdm2SurveyFilledOut: boolean;
  bdm2Outcome: Bdm2OutcomeResDto | null;
  bdm2SampleDeadline: DateTime | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: any) => string;
};

export const stepsData = ({
  dateTime,
  cohortType,
  groupAssignmentDate,
  studyGroup,
  bdm1SurveyFilledOut,
  bdm1Outcome,
  bdm1SampleDeadline,
  retrievalMode,
  bdm2SurveyDue,
  bdm2SurveyFilledOut,
  bdm2Outcome,
  bdm2SampleDeadline,
  t,
}: StepsDataOptions): StepDataType[] => {
  // studyGroup is null only before assignment (steps 0/1), when this builds
  // the step-2 *preview* entry. That preview only uses the group-agnostic
  // title/short description; the group-specific long text is rendered only when
  // step 2 is active, which the store gates on an assigned group. So the
  // placeholder below is never shown as group content — it just fills the type.
  const groupKey = STUDY_GROUP_STEP_KEY[studyGroup ?? StudyGroup.GROUP_2];
  const isMail = (outcome: string) => outcome.startsWith('mail_');

  let assignmentSentence = t('Steps.1.AssignmentSentenceWithoutDate');
  if (groupAssignmentDate !== null) {
    assignmentSentence = t('Steps.1.AssignmentSentence', {
      assignmentDate: groupAssignmentDate,
    });
  }

  // Per-outcome BDM1 paragraphs. A paragraph whose interpolation value is
  // missing (null cohort anchor) is dropped instead of rendering a raw
  // {{placeholder}}.
  const bdm1OutcomeParagraphs = (outcome: Bdm1Outcome): string[] => {
    // The retrieval mode wins over the outcome: a revoked Nachversand eligibility turns the
    // shipment into a shop pickup while bdm1Outcome keeps the SAMPLE_MAIL the participant
    // won, and the banner that carried the mail copy is gone. Without it the participant is
    // told by SMS to collect the sample and finds nothing about it in their account.
    const effectiveOutcome =
      retrievalMode === RetrievalMode.PICKUP
        ? Bdm1Outcome.SAMPLE_LOCAL
        : outcome;
    const outcomeKey = BDM1_OUTCOME_STEP_KEY[effectiveOutcome];
    const deadline = bdm1SampleDeadline?.toFormat('dd.MM.yyyy') ?? null;
    switch (effectiveOutcome) {
      case Bdm1Outcome.VOUCHER:
        return [t(`Steps.bdm1.${outcomeKey}.DescriptionLong1`)];
      case Bdm1Outcome.SAMPLE_LOCAL:
        if (deadline === null) {
          return [];
        }
        return [
          t(`Steps.bdm1.${outcomeKey}.DescriptionLong1`, {
            pickupDeadline: deadline,
            pickupLocation: SAMPLE_LOCATION_NAME,
          }),
        ];
      // SAMPLE_MAIL is the one outcome with something to do: booking the video
      // call. Its copy and the CTA live entirely in the Nachversand banner, so
      // the step keeps only the "what comes next" paragraphs.
      // VOUCHER and SAMPLE_LOCAL have no banner and keep their text here.
      case Bdm1Outcome.SAMPLE_MAIL:
        return [];
    }
  };

  const bdm1Paragraphs = [
    ...(bdm1Outcome !== null ? bdm1OutcomeParagraphs(bdm1Outcome) : []),
    t('Steps.bdm1.NextSurveyInfo'),
    t('Steps.bdm1.Thanks'),
  ];

  // What each variant's copy actually names. The sets differ: the *_full_product texts
  // spell the 20 CHF out and have no rest to name, only the *_cash texts split the rest
  // into voucher, cash and their sum.
  const bdm2OutcomeArgs = (outcome: Bdm2OutcomeResDto) => {
    const drawn = {
      drawn_product: outcome.drawnProduct,
      drawn_surveyround: outcome.drawnSurveyRound,
    };
    const prices = {
      ...drawn,
      drawn_price: outcome.drawnPrice,
      maxprice_of_drawnproduct: outcome.maxPriceOfDrawnProduct,
    };
    switch (outcome.variant) {
      case 'pickupFullProduct':
      case 'mailFullProduct':
        return drawn;
      case 'noProduct':
        return prices;
      case 'pickupNoCash':
      case 'mailNoCash':
        return { ...prices, galaxus_value: outcome.galaxusValue };
      case 'pickupCash':
      case 'mailCash':
        return {
          ...prices,
          galaxus_value: outcome.galaxusValue,
          cash_money: outcome.cashMoney,
          // the copy names the whole rest before splitting it into voucher and cash
          total_rest: outcome.galaxusValue + outcome.cashMoney,
        };
    }
  };

  // Per-variant BDM2 result paragraphs. Same rule as BDM1: a paragraph whose
  // interpolation value is missing (null cohort anchor) is dropped instead of
  // rendering a raw {{placeholder}}.
  const bdm2OutcomeParagraphs = (outcome: Bdm2OutcomeResDto): string[] => {
    const outcomeKey = BDM2_OUTCOME_STEP_KEY[outcome.variant];
    const args = bdm2OutcomeArgs(outcome);

    // Only the pickup copy names a date: noProduct has nothing to hand over, and the
    // mail copy leaves the video call (deadline and CTA) to the Nachversand banner.
    if (outcome.variant === 'noProduct' || isMail(outcomeKey)) {
      return [t(`Steps.bdm2.${outcomeKey}.DescriptionLong1`, args)];
    }
    const deadline = bdm2SampleDeadline?.toFormat('dd.MM.yyyy') ?? null;
    if (deadline === null) {
      return [];
    }
    return [
      t(`Steps.bdm2.${outcomeKey}.DescriptionLong1`, {
        ...args,
        pickupDeadline: deadline,
        pickupLocation: SAMPLE_LOCATION_NAME,
      }),
    ];
  };

  // BDM2 tri-state: per-outcome result copy wins over the thank-you after the
  // fillout, which wins over the fill-out prompt while due. A pickup outcome whose
  // deadline anchor is missing keeps no result paragraph and falls back to the
  // thank-you instead of leaving the step without any BDM2 copy.
  const bdm2ResultParagraphs =
    bdm2Outcome !== null ? bdm2OutcomeParagraphs(bdm2Outcome) : [];
  let bdm2StepContent: { titleLong: string; paragraphs: string[] };
  if (bdm2ResultParagraphs.length > 0) {
    bdm2StepContent = {
      titleLong: t('Steps.bdm2.TitleLong'),
      paragraphs: [...bdm2ResultParagraphs, t('Steps.bdm2.LongtermOutlook')],
    };
  } else if (bdm2SurveyFilledOut || bdm2Outcome !== null) {
    bdm2StepContent = {
      titleLong: t('Steps.bdm2.TitleLong'),
      paragraphs: [t('Steps.bdm2.FilledOut.DescriptionLong1')],
    };
  } else {
    bdm2StepContent = {
      titleLong: t('Steps.bdm2.Due.TitleLong'),
      paragraphs: [t('Steps.bdm2.Due.DescriptionLong1')],
    };
  }

  // Each entry: [condition, stepData]
  // Steps are defined in order. Conditional steps use `false` to be filtered out.
  const allSteps: [boolean, StepDataType][] = [
    // Step 0: Enrollment questionnaire
    [
      true,
      {
        key: StepKey.Step0,
        title: t('Steps.0.Title'),
        title_long: t('Steps.0.TitleLong'),
        description: t('Steps.0.DescriptionShort'),
        description_long: descriptionLong([
          t('Steps.0.DescriptionLong1'),
          t('Steps.0.DescriptionLong2'),
        ]),
        img: fillOut_img,
      },
    ],

    // Step 1: Assignment to study parts
    [
      true,
      {
        key: StepKey.Step1,
        title: t('Steps.1.Title'),
        title_long: t('Steps.1.TitleLong'),
        description: t('Steps.1.DescriptionShort'),
        description_long: descriptionLong([
          t('Steps.1.DescriptionLong1', { assignmentSentence }),
        ]),
        img: waiting_empty_img,
      },
    ],

    // Step 2: Study group info (Group 1 or Group 2 variant)
    [
      true,
      {
        key: StepKey.Step2,
        title: t(`Steps.2.${groupKey}.Title`),
        title_long: t(`Steps.2.${groupKey}.TitleLong`),
        description: t('Steps.2.DescriptionShort'),
        description_long: descriptionLong([
          t(`Steps.2.${groupKey}.DescriptionLong1`),
          t(`Steps.2.${groupKey}.DescriptionLong2`),
        ]),
        img: fillOut_img,
      },
    ],

    // Step 3 (conditional): After BDM 1 — Group 1 once survey 1 is filled out.
    // The outcome may lag behind the fillout (SCC-815 cron); until it arrives
    // only the generic thank-you is shown.
    [
      isBdm1StepVisible(studyGroup, bdm1SurveyFilledOut, bdm1Outcome),
      {
        key: StepKey.StepBdm1,
        title: t('Steps.bdm1.Title'),
        title_long: t('Steps.bdm1.TitleLong'),
        description: t('Steps.bdm1.Title'),
        description_long: descriptionLong(bdm1Paragraphs),
        img: waiting_empty_img,
      },
    ],

    // Step 4 (conditional): BDM 2 — fill-out prompt from the day-28 offset on,
    // thank-you after the fillout, per-variant result copy once the outcome is imported.
    [
      isBdm2StepVisible(
        studyGroup,
        bdm2SurveyDue,
        bdm2SurveyFilledOut,
        bdm2Outcome,
      ),
      {
        key: StepKey.StepBdm2,
        title: t('Steps.bdm2.Title'),
        title_long: bdm2StepContent.titleLong,
        description: t('Steps.bdm2.Title'),
        description_long: descriptionLong(bdm2StepContent.paragraphs),
        img: waiting_empty_img,
      },
    ],

    // Step 5: Long-term study group assignment (translation key: Steps.3)
    [
      true,
      {
        key: StepKey.Step3,
        title: t('Steps.3.Title'),
        title_long: t('Steps.3.TitleLong'),
        description: t('Steps.3.DescriptionShort'),
        description_long: descriptionLong([
          t('Steps.3.DescriptionLong1'),
          t('Steps.3.DescriptionLong2'),
        ]),
        img: waiting_empty_img,
      },
    ],

    // Step 6: Study start (translation key: Steps.4) — only when cohort assigned
    [
      cohortType !== CohortType.NONE,
      {
        key: StepKey.Step4,
        title: t(`Steps.4.${cohortType}.Title`),
        title_long: t(`Steps.4.${cohortType}.TitleLong`),
        description: t('Steps.4.DescriptionShort'),
        description_long: descriptionLong([
          t(`Steps.4.${cohortType}.DescriptionLong1`, {
            date: dateTime.toFormat('dd.MM.'),
          }),
          t(`Steps.4.${cohortType}.DescriptionLong2`),
        ]),
        img: waiting_empty_img,
      },
    ],
  ];

  return allSteps.filter(([condition]) => condition).map(([, step]) => step);
};
